// ═══════════════════════════════════════════════════════════════
// RollCall — Kiosk Mode
// Full-screen, dark navy, PIN pad + QR scanning, auto-dismiss
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from 'react';
import { Select, Button, Group, Text, Modal, TextInput } from '../../components/gab/GabComponents';
import {
  IconClipboardCheck, IconQrcode, IconKeyboard,
  IconCircleCheck, IconCircleX, IconLogout, IconLogin,
  IconArrowLeft, IconLoader,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useAuthStore } from '../../store/authStore';
import { useRollCallStore } from '../../store/rollcallStore';
import PinPad from '../../components/rollcall/PinPad';

type KioskState = 'idle' | 'pin' | 'scan' | 'found' | 'checkout_confirm' | 'confirming';
interface ConfirmInfo {
  type: 'checkin' | 'checkout' | 'error';
  name?: string; agency?: string; position?: string;
  duration?: string; message?: string;
}

export default function KioskMode() {
  const { user, incidents, loadIncidents } = useAuthStore();
  const {
    events, checkins,
    loadEvents, loadCheckins, loadPersonnel,
    checkIn, checkOut, findByPin, findByBadgeId,
  } = useRollCallStore();

  const scannerRef = useRef<HTMLDivElement>(null);
  const html5QrRef = useRef<any>(null);

  const openEvents = events.filter(e => e.eventStatus === 'open');
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [mode, setMode] = useState<KioskState>('idle');
  const [confirmState, setConfirmState] = useState<ConfirmInfo | null>(null);
  const [foundPerson, setFoundPerson] = useState<{ id: number; firstName: string; lastName: string; agency: string; position: string } | null>(null);
  const [activeCheckinId, setActiveCheckinId] = useState<number | null>(null);
  const [activeCheckinTime, setActiveCheckinTime] = useState<string>('');
  const [processing, setProcessing] = useState(false);
  const [now, setNow] = useState(new Date());
  const [showExitModal, setShowExitModal] = useState(false);
  const [exitPin, setExitPin] = useState('');
  useEffect(() => {
    loadIncidents(); loadEvents(); loadCheckins(); loadPersonnel();
  }, []);

  // Clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-dismiss confirmation after 4s
  useEffect(() => {
    if (confirmState) {
      const t = setTimeout(() => { setConfirmState(null); setFoundPerson(null); setActiveCheckinId(null); setMode('idle'); }, 4000);
      return () => clearTimeout(t);
    }
  }, [confirmState]);

  const selectedEvent = openEvents.find(e => String(e.id) === selectedEventId);
  const incidentName = selectedEvent
    ? incidents.find(i => String(i.id) === String(selectedEvent.incidentId))?.name || ''
    : '';

  const handlePersonFound = useCallback(async (person: typeof foundPerson) => {
    if (!person || !selectedEventId) return;
    setFoundPerson(person);
    setProcessing(true);
    // Check if already checked in
    const existing = checkins.find(
      c => String(c.eventId) === selectedEventId && String(c.personnelId) === String(person.id) && !c.checkOutTime
    );
    if (existing) {
      setActiveCheckinId(existing.id);
      setActiveCheckinTime(existing.checkInTime);
    } else {
      setActiveCheckinId(null);
      setActiveCheckinTime('');
    }
    setProcessing(false);
    setMode('found');
  }, [selectedEventId, checkins]);

  const handleCheckIn = useCallback(async () => {
    if (!foundPerson || !selectedEventId) return;
    setProcessing(true);
    try {
      await checkIn({
        eventId: selectedEventId,
        personnelId: String(foundPerson.id),
        personnelName: `${foundPerson.firstName} ${foundPerson.lastName}`,
        agency: foundPerson.agency,
        position: foundPerson.position,
        method: 'pin',
      });
      setConfirmState({ type: 'checkin', name: `${foundPerson.firstName} ${foundPerson.lastName}`, agency: foundPerson.agency, position: foundPerson.position });
      setMode('confirming');
    } catch (err: any) {
      setConfirmState({ type: 'error', message: err?.message || 'Check-in failed' });
      setMode('confirming');
    } finally {
      setProcessing(false);
    }
  }, [selectedEventId, checkIn, foundPerson]);

  const handleConfirmCheckOut = useCallback(async () => {
    if (!foundPerson || !activeCheckinId) return;
    setProcessing(true);
    try {
      await checkOut(activeCheckinId, user?.fullName ?? 'kiosk');
      const duration = activeCheckinTime
        ? `${Math.round((Date.now() - new Date(activeCheckinTime).getTime()) / 60000)} min`
        : '';
      setConfirmState({ type: 'checkout', name: `${foundPerson.firstName} ${foundPerson.lastName}`, agency: foundPerson.agency, position: foundPerson.position, duration });
      setMode('confirming');
    } catch (err: any) {
      setConfirmState({ type: 'error', message: err?.message || 'Check-out failed' });
      setMode('confirming');
    } finally {
      setProcessing(false);
    }
  }, [checkOut, foundPerson, activeCheckinId, activeCheckinTime, user]);

  const handlePinSubmit = useCallback(async (pin: string) => {
    const person = findByPin(pin);
    if (person) {
      await handlePersonFound(person);
    } else {
      setConfirmState({ type: 'error', message: 'Invalid PIN — Person not found' });
      setMode('confirming');
    }
  }, [findByPin, handlePersonFound]);

  // QR Scanner setup
  useEffect(() => {
    if (mode === 'scan' && scannerRef.current && !html5QrRef.current) {
      import('html5-qrcode').then(({ Html5Qrcode }) => {
        const scanner = new Html5Qrcode('kiosk-scanner');
        html5QrRef.current = scanner;
        scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          async (text: string) => {
            scanner.stop().catch(() => {});
            html5QrRef.current = null;
            let person = findByBadgeId(text);
            if (!person && text.startsWith('RC-PIN:')) {
              person = findByPin(text.replace('RC-PIN:', ''));
            }
            if (person) {
              await handlePersonFound(person);
            } else {
              setConfirmState({ type: 'error', message: 'Badge not recognized' });
              setMode('confirming');
            }
          },
          () => { /* ignore scan errors (no QR in frame) */ },
        ).catch(() => {
          setConfirmState({ type: 'error', message: 'Camera access denied' });
          setMode('confirming');
        });
      });
    }
    return () => {
      if (html5QrRef.current) {
        html5QrRef.current.stop().catch(() => {});
        html5QrRef.current = null;
      }
    };
  }, [mode, findByBadgeId, findByPin, handlePersonFound]);

  const resetToIdle = () => {
    setFoundPerson(null); setActiveCheckinId(null); setConfirmState(null); setMode('idle');
  };

  const handleExit = () => {
    const validPins = ['1234', '0000', '000000'];
    if (validPins.includes(exitPin)) {
      window.location.href = '/rollcall';
    } else {
      notifications.show({ message: 'Invalid admin PIN. Default: 1234', color: 'var(--color-error)' });
      setExitPin('');
    }
  };

  // Setup screen — select event
  if (!selectedEventId) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #1a365d 0%, #112D4E 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 420, width: '100%', padding: 32 }}>
          <div style={{ width: 64, height: 64, borderRadius: 16, background: 'rgba(255,255,255,0.1)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
            <IconClipboardCheck size={28} color="#fff" />
          </div>
          <Text style={{ color: '#fff', fontSize: 32, fontWeight: 800, letterSpacing: 4, marginBottom: 4 }}>ROLLCALL</Text>
          <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, marginBottom: 32 }}>Kiosk Mode Setup</Text>
          <Select placeholder="Select an event"
            data={openEvents.map(e => ({ value: String(e.id), label: `${e.eventName} — ${e.eventDate || 'No date'}` }))}
            value={selectedEventId} onChange={setSelectedEventId} size="lg"
            styles={{ input: { background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' } }} />
          <Group justify="center" mt="xl">
            <Button size="lg" variant="subtle" color="#939598" style={{ color: 'rgba(255,255,255,0.5)' }}
              onClick={() => { window.location.href = '/rollcall'; }}>
              <IconArrowLeft size={16} style={{ marginRight: 8 }} /> Exit
            </Button>
          </Group>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #1a365d 0%, #112D4E 100%)', color: '#fff', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Text style={{ fontSize: 16, fontWeight: 800, letterSpacing: 3 }}>ROLLCALL</Text>
        <div style={{ textAlign: 'center' }}>
          <Text style={{ fontSize: 14, fontWeight: 600 }}>{selectedEvent?.name}</Text>
          <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{incidentName}</Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Text style={{ fontFamily: "'DM Mono', monospace", fontSize: 20, fontWeight: 600 }}>
            {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </Text>
          <Button size="xs" variant="subtle" color="#939598" style={{ color: 'rgba(255,255,255,0.4)' }}
            onClick={() => setShowExitModal(true)}>
            <IconLogout size={16} />
          </Button>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32 }}>

        {/* Confirmation overlay */}
        {confirmState && mode === 'confirming' && (
          <div style={{ textAlign: 'center' }}>
            {confirmState.type === 'checkin' && (
              <>
                <IconCircleCheck size={80} style={{ color: '#16a34a', marginBottom: 16 }} />
                <Text style={{ fontSize: 32, fontWeight: 700, marginBottom: 4 }}>{confirmState.name}</Text>
                <Text style={{ fontSize: 24, color: '#86efac', marginBottom: 8 }}>Checked In</Text>
                <Text style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>
                  {confirmState.agency} · {(confirmState.position || '').replace(/_/g, ' ')}
                </Text>
              </>
            )}
            {confirmState.type === 'checkout' && (
              <>
                <IconLogout size={80} style={{ color: '#3b82f6', marginBottom: 16 }} />
                <Text style={{ fontSize: 32, fontWeight: 700, marginBottom: 4 }}>{confirmState.name}</Text>
                <Text style={{ fontSize: 24, color: '#93c5fd', marginBottom: 8 }}>Checked Out</Text>
                {confirmState.duration && <Text style={{ fontSize: 16, color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>Duration: {confirmState.duration}</Text>}
                <Text style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>
                  {confirmState.agency} · {(confirmState.position || '').replace(/_/g, ' ')}
                </Text>
              </>
            )}
            {confirmState.type === 'error' && (
              <>
                <IconCircleX size={80} style={{ color: '#B12525', marginBottom: 16 }} />
                <Text style={{ fontSize: 24, fontWeight: 700, color: '#fca5a5', marginBottom: 8 }}>{confirmState.message}</Text>
                <Text style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>Please try again</Text>
              </>
            )}
          </div>
        )}

        {/* Person found */}
        {mode === 'found' && foundPerson && (
          <div style={{ textAlign: 'center', maxWidth: 420 }}>
            <div style={{
              width: 80, height: 80, borderRadius: '50%', background: 'rgba(63,114,175,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
              fontSize: 32, fontWeight: 700,
            }}>
              {foundPerson.firstName[0]}{foundPerson.lastName[0]}
            </div>
            <Text style={{ fontSize: 32, fontWeight: 700, marginBottom: 4 }}>{foundPerson.firstName} {foundPerson.lastName}</Text>
            <Text style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', marginBottom: 24 }}>
              {foundPerson.agency} · {(foundPerson.position || '').replace(/_/g, ' ')}
            </Text>

            {activeCheckinId ? (
              <>
                <div style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 4, padding: '16px 24px', marginBottom: 24 }}>
                  <Text style={{ fontSize: 14, color: '#93c5fd' }}>
                    Checked in {activeCheckinTime ? `at ${new Date(activeCheckinTime).toLocaleTimeString()}` : 'earlier'}
                  </Text>
                </div>
                <Group justify="center" gap="lg">
                  <button onClick={() => setMode('checkout_confirm')} disabled={processing} style={{
                    width: 180, height: 120, borderRadius: 16, border: '2px solid rgba(59,130,246,0.4)',
                    background: 'rgba(59,130,246,0.1)', color: '#fff', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
                    fontFamily: "'DM Sans', sans-serif",
                  }}>
                    <IconLogout size={32} style={{ color: '#3b82f6' }} />
                    <span style={{ fontSize: 16, fontWeight: 700 }}>Check Out</span>
                  </button>
                  <button onClick={resetToIdle} style={{
                    width: 120, height: 120, borderRadius: 16, border: '2px solid rgba(255,255,255,0.15)',
                    background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
                    fontFamily: "'DM Sans', sans-serif",
                  }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>Cancel</span>
                  </button>
                </Group>
              </>
            ) : (
              <Group justify="center" gap="lg">
                <button onClick={handleCheckIn} disabled={processing} style={{
                  width: 180, height: 120, borderRadius: 16, border: '2px solid rgba(22,163,74,0.4)',
                  background: 'rgba(22,163,74,0.1)', color: '#fff', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
                  fontFamily: "'DM Sans', sans-serif",
                }}>
                  <IconLogin size={32} style={{ color: '#16a34a' }} />
                  <span style={{ fontSize: 16, fontWeight: 700 }}>Check In</span>
                </button>
                <button onClick={resetToIdle} style={{
                  width: 120, height: 120, borderRadius: 16, border: '2px solid rgba(255,255,255,0.15)',
                  background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
                  fontFamily: "'DM Sans', sans-serif",
                }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>Cancel</span>
                </button>
              </Group>
            )}
          </div>
        )}

        {/* Checkout confirmation */}
        {mode === 'checkout_confirm' && foundPerson && (
          <div style={{ textAlign: 'center', maxWidth: 420 }}>
            <IconLogout size={48} style={{ color: '#3b82f6', marginBottom: 16 }} />
            <Text style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Confirm Check Out</Text>
            <Text style={{ fontSize: 20, marginBottom: 24 }}>{foundPerson.firstName} {foundPerson.lastName}</Text>
            <Group justify="center" gap="lg">
              <Button size="lg" color="var(--color-primary-700)" onClick={handleConfirmCheckOut} loading={processing}>Yes, Check Out</Button>
              <Button size="lg" variant="subtle" color="#939598" style={{ color: 'rgba(255,255,255,0.6)' }} onClick={() => setMode('found')}>Go Back</Button>
            </Group>
          </div>
        )}

        {/* Processing */}
        {processing && !['found', 'checkout_confirm', 'confirming'].includes(mode) && (
          <div style={{ textAlign: 'center' }}>
            <IconLoader size={48} style={{ color: '#3b82f6', marginBottom: 16 }} className="spin" />
            <Text style={{ fontSize: 20, color: 'rgba(255,255,255,0.6)' }}>Looking up...</Text>
          </div>
        )}

        {/* Idle — mode selection */}
        {!confirmState && mode === 'idle' && (
          <div style={{ textAlign: 'center' }}>
            <Text style={{ fontSize: 32, fontWeight: 700, marginBottom: 32, opacity: 0.8 }}>Scan Badge or Enter PIN</Text>
            <Group gap="xl" justify="center">
              <button onClick={() => setMode('scan')} style={{
                width: 180, height: 180, borderRadius: 4, border: '2px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)', color: '#fff', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
                fontFamily: "'DM Sans', sans-serif",
              }}>
                <IconQrcode size={48} style={{ opacity: 0.7 }} />
                <span style={{ fontSize: 16, fontWeight: 600 }}>Scan Badge</span>
              </button>
              <button onClick={() => setMode('pin')} style={{
                width: 180, height: 180, borderRadius: 4, border: '2px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)', color: '#fff', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
                fontFamily: "'DM Sans', sans-serif",
              }}>
                <IconKeyboard size={48} style={{ opacity: 0.7 }} />
                <span style={{ fontSize: 16, fontWeight: 600 }}>Enter PIN</span>
              </button>
            </Group>
          </div>
        )}

        {/* PIN entry */}
        {!confirmState && mode === 'pin' && (
          <div style={{ textAlign: 'center' }}>
            <Text style={{ fontSize: 20, fontWeight: 600, marginBottom: 24, opacity: 0.7 }}>Enter your PIN</Text>
            <PinPad onSubmit={handlePinSubmit} />
            <Button variant="subtle" color="#939598" mt="xl" style={{ color: 'rgba(255,255,255,0.4)' }} onClick={resetToIdle}>
              <IconArrowLeft size={14} style={{ marginRight: 8 }} /> Back
            </Button>
          </div>
        )}

        {/* Badge scan — camera QR reader */}
        {!confirmState && mode === 'scan' && (
          <div style={{ textAlign: 'center' }}>
            <Text style={{ fontSize: 20, fontWeight: 600, marginBottom: 24, opacity: 0.7 }}>Hold badge up to camera</Text>
            <div ref={scannerRef} style={{ width: 320, height: 320, borderRadius: 16, overflow: 'hidden', margin: '0 auto', background: 'rgba(0,0,0,0.3)' }}>
              <div id="kiosk-scanner" style={{ width: '100%', height: '100%' }} />
            </div>
            <Button variant="subtle" color="#939598" mt="xl" style={{ color: 'rgba(255,255,255,0.4)' }} onClick={resetToIdle}>
              <IconArrowLeft size={14} style={{ marginRight: 8 }} /> Back
            </Button>
          </div>
        )}
      </div>

      {/* Exit modal */}
      <Modal opened={showExitModal} onClose={() => { setShowExitModal(false); setExitPin(''); }} title="Exit Kiosk Mode" centered size="sm">
        <Text size="sm" mb="md">Enter admin PIN to exit kiosk mode.</Text>
        <TextInput type="password" placeholder="Admin PIN" value={exitPin}
          onChange={e => setExitPin(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleExit()} mb="md" />
        <Group justify="flex-end">
          <Button variant="light" onClick={() => { setShowExitModal(false); setExitPin(''); }}>Cancel</Button>
          <Button onClick={handleExit}>Exit</Button>
        </Group>
      </Modal>
    </div>
  );
}
